import React, { useEffect, useState } from 'react';
import { Stack, Autocomplete, TextField, Checkbox, Skeleton, Alert } from '@mui/material';
import { Box, pn } from '@compass/core';
import { Select } from 'components';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {
    StudentFilter as StudentFilterModel,
    StudentFilterResource,
    LkStudentDisplayFilterId,
} from 'generated/openApi';
import { useEndReasonTypeData } from '../contexts/EndReasonTypeDataContext';
import ApiService from 'services/apiService';
import { DashboardYearFilter } from '../components/DashboardYearFilter';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const filterSx = {
    minWidth: 220,
    flex: { xs: '1 1 220px', lg: '1 1 calc(50% - 4px)' },
    maxWidth: { lg: 'calc(50% - 4px)' },
};

export const EndReasonTypeFilterControls = () => {
    const { 
        filter, 
        setFilter, 
        activeSchoolYear, 
        endReasonOptions, 
        optionsLoading,
        optionsError,
    } = useEndReasonTypeData();

    // Fetch StudentFilterResource for program filter
    const [resource, setResource] = useState<StudentFilterResource>({} as StudentFilterResource);
    const properties = resource.properties || [];

    useEffect(() => {
        ApiService.students.getFilterResource().then((response) => {
            setResource(response);
        });
    }, []);

    const programField = () => {
        const propertyName = pn<StudentFilterModel>("programIds");
        const property = properties.find((x) => x.propertyName === propertyName);

        return (
            <Select
                modelProperty={property}
                value={resource.programs?.find((x) => x.id === filter.programId)}
                options={resource.programs}
                getOptionLabel={(option) => option.name}
                onChange={(e, value) => setFilter({ programId: value?.id ?? null })}
                size="small"
            />
        );
    };

    // Check if tenant should see the program filter
    const showProgramFilter = resource.displayFilters?.some(
        x => x.id === LkStudentDisplayFilterId.Program
    );

    if (optionsLoading) {
        return (
            <Stack spacing={2} sx={{ mb: 2 }}>
                <Skeleton variant="rounded" height={40} />
                <Skeleton variant="rounded" height={40} />
            </Stack>
        );
    }

    if (optionsError) {
        return <Alert severity="error" sx={{ mb: 2 }}>{optionsError}</Alert>;
    }

    if (!activeSchoolYear) return null;

    const selectedTypeOptions = endReasonOptions.filter(t => filter.selectedEndReasonTypes?.includes(t.id));
    const selectedGradeLevelOptions = resource.gradeLevels?.filter(g => filter.gradeLevelIds.includes(g.id)) || [];

    return (
        <Stack spacing={2} sx={{ mb: 2 }}>
            <DashboardYearFilter
                idPrefix="end-reason-type"
                years={filter.schoolYears}
                activeSchoolYear={activeSchoolYear}
                onApply={schoolYears => setFilter({ schoolYears })}
                grow
            >
                <Autocomplete
                    multiple
                    size="small"
                    sx={filterSx}
                    options={resource.gradeLevels || []}
                    value={selectedGradeLevelOptions}
                    onChange={(_, newValue) => setFilter({ gradeLevelIds: newValue.map(v => v.id) })}
                    disableCloseOnSelect
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    limitTags={2}
                    renderOption={(props, option, { selected }) => (
                        <li {...props}>
                            <Checkbox icon={icon} checkedIcon={checkedIcon} sx={{ marginRight: 8 }} checked={selected} />
                            {option.name}
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Grade Level" placeholder="All Grade Levels" />}
                />
                {showProgramFilter && (
                    <Box sx={filterSx}>
                        {programField()}
                    </Box>
                )}
                <Autocomplete
                    multiple
                    size="small"
                    sx={filterSx}
                    options={endReasonOptions}
                    value={selectedTypeOptions}
                    onChange={(_, newValue) => setFilter({ selectedEndReasonTypes: newValue.map(v => v.id) })}
                    disableCloseOnSelect
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    limitTags={2}
                    renderOption={(props, option, { selected }) => (
                        <li {...props}>
                            <Checkbox icon={icon} checkedIcon={checkedIcon} sx={{ marginRight: 8 }} checked={selected} />
                            {option.name}
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="End Reason Type" placeholder="All Types" />}
                />
            </DashboardYearFilter>
        </Stack>
    );
};
